import React, { useState } from 'react';

const UserAgreementModal = ({ onAgree, onCancel, onClose, viewOnly = false }) => {
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    setIsChecked(e.target.checked);
  };

  const handleAgree = (e) => {
    e.stopPropagation();
    if (isChecked) {
      onAgree();
    }
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    onCancel();
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  // Prevent clicks inside the modal from closing it
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[3000]"
      onClick={viewOnly ? handleClose : handleCancel}
    >
      <div
        className="bg-white p-6 rounded-lg shadow-lg max-w-4xl w-full"
        onClick={handleModalClick}
      >
        <h2 className="text-2xl font-bold mb-4">User Agreement</h2>

        <div className="bg-gray-50 p-6 rounded-md mb-6 max-h-[70vh] overflow-y-auto">
          <div className="prose prose-sm max-w-none">
            <h3 className="text-xl font-semibold mb-4">TERMS OF USE AGREEMENT</h3>
            <div className="mb-4">
              <p className="font-medium">Modern Technology LLC</p>
              <p className="text-gray-600">Effective Date: February 1, 2025</p>
            </div>

            <p className="mb-6">
              Welcome to Modern Technology LLC ("Company," "we," "our," or "us"). These Terms of Use ("Agreement") govern your access to and use of our Yard Management System ("System" or "Service"). By accessing or using the Service, you agree to comply with and be bound by these Terms. If you do not agree, do not use the Service.
            </p>

            <div className="space-y-6">
              <section>
                <h4 className="font-bold text-lg mb-2">1. DEFINITIONS</h4>
                <ul className="list-none space-y-2">
                  <li><span className="font-medium">User:</span> Any individual or entity accessing the System.</li>
                  <li><span className="font-medium">Service:</span> The online yard management system provided by Modern Technology LLC.</li>
                  <li><span className="font-medium">Account:</span> A registered profile required to use the Service.</li>
                  <li><span className="font-medium">Data:</span> Any information collected, stored, or processed through the Service.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">2. ACCOUNT REGISTRATION & RESPONSIBILITIES</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Users must provide accurate and complete information during registration.</li>
                  <li>Users are responsible for maintaining the confidentiality of login credentials.</li>
                  <li>The Company reserves the right to suspend or terminate accounts for violations of this Agreement.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">3. PERMITTED & PROHIBITED USES</h4>
                <div className="mb-3">
                  <p className="font-medium mb-2">Permitted Uses:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Managing yard operations, tracking shipments, and optimizing logistics.</li>
                    <li>Integrating with approved third-party services for enhanced functionality.</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium mb-2">Prohibited Uses:</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Unauthorized access, modification, or distribution of the Service.</li>
                    <li>Engaging in fraudulent activities or misleading reporting.</li>
                    <li>Reverse engineering, copying, or attempting to exploit vulnerabilities in the System.</li>
                  </ul>
                </div>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">4. FEES & PAYMENTS</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Use of the Service may require subscription fees, payable as per the pricing plan selected.</li>
                  <li>Failure to make timely payments may result in suspension or termination of access.</li>
                  <li>All fees are non-refundable unless otherwise stated in writing by the Company.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">5. DATA PRIVACY & SECURITY</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Company collects and processes user data as described in our Privacy Policy.</li>
                  <li>Users retain ownership of their data but grant the Company a limited license to use data for operational and analytical purposes.</li>
                  <li>The Company employs industry-standard security measures but does not guarantee complete security.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">6. SERVICE AVAILABILITY & MAINTENANCE</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Company strives for 99.9% uptime but may conduct maintenance that results in temporary service disruptions.</li>
                  <li>Users will be notified of scheduled maintenance whenever possible.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">7. THIRD-PARTY INTEGRATIONS</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Service may integrate with third-party applications, such as GPS tracking and payment processors.</li>
                  <li>The Company is not responsible for the reliability or security of third-party services.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">8. INTELLECTUAL PROPERTY RIGHTS</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Company retains all ownership rights to the Service, including software, trademarks, and content.</li>
                  <li>Users may not copy, modify, or distribute any part of the Service without prior written consent.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">9. LIMITATION OF LIABILITY & DISCLAIMERS</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Company provides the Service "as is" and makes no warranties regarding uninterrupted access or error-free operation.</li>
                  <li>The Company is not liable for indirect, incidental, or consequential damages arising from the use of the Service.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">10. TERMINATION & SUSPENSION</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Company reserves the right to suspend or terminate accounts for violations of this Agreement or failure to pay fees.</li>
                  <li>Users may terminate their accounts by providing written notice, but no refunds will be issued.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">11. INDEMNIFICATION</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Users agree to indemnify and hold harmless the Company from any claims, damages, or liabilities resulting from misuse of the Service.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">12. COMPLIANCE WITH LAWS</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Users must comply with all applicable laws, including DOT and FMCSA regulations where applicable.</li>
                  <li>The Company is not responsible for user compliance with legal requirements.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">13. DISPUTE RESOLUTION</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Any disputes arising from this Agreement shall be resolved through arbitration in [State/Country of Company's Incorporation].</li>
                  <li>Users waive the right to participate in class-action lawsuits against the Company.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">14. CHANGES TO THIS AGREEMENT</h4>
                <ul className="list-disc pl-6 space-y-1">
                  <li>The Company reserves the right to modify these Terms at any time. Users will be notified of significant changes.</li>
                  <li>Continued use of the Service after modifications constitutes acceptance of the updated Terms.</li>
                </ul>
              </section>

              <section>
                <h4 className="font-bold text-lg mb-2">15. CONTACT INFORMATION</h4>
                <p>For any questions or concerns regarding these Terms, contact us at:</p>
                <p className="mt-2 font-semibold text-blue-600">Modern Technology LLC</p>
                <p className="font-semibold text-blue-600">JZHANG@MODERNTECH.DEV</p>
                {/* <p>[Company Phone Number]</p> */}
              </section>
            </div>
          </div>
        </div>

        {!viewOnly && (
          <div className="mb-6">
            <label className="flex items-center" htmlFor="agree-terms">
              <input
                id="agree-terms"
                type="checkbox"
                checked={isChecked}
                onChange={handleCheckboxChange}
                className="form-checkbox h-5 w-5 text-blue-600"
              />
              <span className="ml-2 text-gray-700">
                I have read and agree to the terms and conditions
              </span>
            </label>
          </div>
        )}

        <div className="flex justify-end space-x-4">
          {viewOnly ? (
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Close
            </button>
          ) : (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleAgree}
                disabled={!isChecked}
                className={`px-4 py-2 rounded ${
                  isChecked
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Accept
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAgreementModal;
